/**
 * Date helper utilities
 */

import i18n from '../../../i18n';

/**
 * Checks if a game is considered "new" based on its activation or lobby addition date.
 * A game is new if it was activated/added within the last 60 days.
 * 
 * @param activatedOn - ISO date string of when the game was activated
 * @param addedToLobbyOn - ISO date string of when the game appeared in the casino lobby
 * @returns true if the most recent date (activatedOn or addedToLobbyOn) is within the last 60 days, false otherwise
 */
export const isGameNew = (activatedOn: string | null | undefined, addedToLobbyOn: string | null | undefined): boolean => {
  if (!activatedOn && !addedToLobbyOn) return false;
  
  const now = new Date();
  const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

  const activationDate = activatedOn ? new Date(activatedOn) : null;
  const lobbyAdditionDate = addedToLobbyOn ? new Date(addedToLobbyOn) : null;

  const mostRecentDate = activationDate && lobbyAdditionDate
    ? new Date(Math.max(activationDate.getTime(), lobbyAdditionDate.getTime()))
    : activationDate
      ? activationDate
      : lobbyAdditionDate;
  
  if (!mostRecentDate) return false;
  
  return mostRecentDate >= sixtyDaysAgo && mostRecentDate <= now;
};

/**
 * Formats a date to a localized date format with translated month (DD MMM YYYY).
 *
 * @param date - ISO date string or Date object to format
 * @param options - Optional formatting options
 * @returns Formatted date string (e.g., "06 Jan 2026" or "06 Jan")
 */
export const formatDate = (
  date: string | Date | undefined,
  options?: {
    includeYear?: boolean;
  }
): string => {
  if (!date) return '';

  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return '';

  const day = String(dateObj.getUTCDate()).padStart(2, '0');
  const monthAbbr = getMonthAbbr(dateObj);
  const translatedMonth = i18n.t(`months.${monthAbbr}`, monthAbbr);
  const includeYear = options?.includeYear !== false;

  return includeYear
    ? `${day} ${translatedMonth} ${dateObj.getUTCFullYear()}`
    : `${day} ${translatedMonth}`;
};

/**
 * Formats a time string to HH:MM format in local timezone.
 * Converts UTC time strings to user's local time for display.
 * 
 * @param time - Time string ("08:00:00Z" or "2026-03-10T08:00:00Z")
 * @returns Time string in format "HH:MM" (local time)
 */
export const formatTime = (time: string): string => {
  let date: Date;

  if (time.includes('T')) {
    date = new Date(time);
  } else {
    const cleanTime = time.replace('Z', '');
    const [hours, minutes] = cleanTime.split(':').map(Number);
    date = new Date();
    date.setUTCHours(hours, minutes, 0, 0);
  }

  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Get month abbreviation from a date
 * 
 * @param date - Date object to get month from
 * @returns Month abbreviation (e.g., "jan", "feb", "mar")
 */
export const getMonthAbbr = (date: Date): string => {
  const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  return monthNames[date.getUTCMonth()];
};

/**
 * Formats a date into a localized string format with translated month names
 * 
 * @param date - The date to format
 * @returns Formatted date string in "DD MMM" format (e.g., "26 Jan") or "Invalid Date" if date is invalid
 */
export const formatDayMonth = (date: Date): string => {
  if (!date || isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  const day = String(date.getUTCDate()).padStart(2, "0");
  const monthAbbr = getMonthAbbr(date);
  const translatedMonth = i18n.t(`months.${monthAbbr}`, monthAbbr);
  return `${day} ${translatedMonth}`;
};
